import React, { useRef, useEffect, useState } from 'react';
import { rowFor } from '../utils/rtl';
import { useRightPaneWidth } from '../hooks/useRightPaneWidth';
import {
  View,
  StyleSheet,
  Platform,
  Dimensions,
  Animated,
  Easing,
  TouchableOpacity,
  Text,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing } from '../theme';
import { useLocale } from '../contexts/LocaleContext';
import type { TranslationKey } from '../i18n/en';
import { ContactsList, Contact as ContactsListContact } from './ContactsList';
import { ContactEditForm } from './ContactEditForm';
import { useAppStore } from '../store/appStore';
import { PANE_ANIMATION_DURATION } from '../constants/paneLayout';
import { useProfileCompletion } from '../hooks/useProfileCompletion';
import { useAppContext } from '../contexts/AppContext';
import { PhoneCallScreen } from './PhoneCallScreen';
import { PhoneCallMinimizedBar } from './PhoneCallMinimizedBar';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { WorkspaceAssistant } from './WorkspaceAssistant';
import { AiBotIcon } from './icons/AppIcon';
import { typography } from '../theme';
import { useWorkspaceAssistantStore } from '../store/workspaceAssistantStore';

const { width: screenWidth } = Dimensions.get('window');
const isWeb = Platform.OS === 'web';
const isMobileBrowser = Platform.OS === 'web' && (screenWidth < 768);

interface Contact {
  id: string;
  name: string;
  channel: 'whatsapp' | 'facebook-messenger' | 'x-twitter' | 'phone' | 'telegram' | 'email';
  phoneNumber?: string;
  email?: string;
  profilePictureUrl?: string;
  isOnline?: boolean;
  isBusiness?: boolean;
  metadata?: any;
}

/** The tab of what the pane shows, by content (custom content says what it is via `tab`). */
const PANE_TABS: Record<string, { icon: keyof typeof Ionicons.glyphMap; active: keyof typeof Ionicons.glyphMap; label: TranslationKey }> = {
  chat: { icon: 'chatbubbles-outline', active: 'chatbubbles', label: 'workspaceAssistant.paneTab.chat' },
  calls: { icon: 'call-outline', active: 'call', label: 'workspaceAssistant.paneTab.calls' },
  form: { icon: 'create-outline', active: 'create', label: 'workspaceAssistant.paneTab.form' },
  contacts: { icon: 'people-outline', active: 'people', label: 'workspaceAssistant.paneTab.contacts' },
  'edit-contact': { icon: 'person-outline', active: 'person', label: 'workspaceAssistant.paneTab.contact' },
  settings: { icon: 'settings-outline', active: 'settings', label: 'workspaceAssistant.paneTab.settings' },
  details: { icon: 'information-circle-outline', active: 'information-circle', label: 'workspaceAssistant.paneTab.details' },
};

interface RightPaneProps {
  isVisible?: boolean;
  onClose?: () => void;
  content?: 'contacts' | 'edit-contact' | 'details' | 'custom' | 'empty' | 'settings';
  customContent?: React.ReactNode;
  /** What custom content is (chat, call history, a form): its tab's icon and label. */
  tab?: 'chat' | 'calls' | 'form';
  selectedContact?: Contact | null;
  onContactSave?: (contact: Contact) => void;
  onContactDelete?: (contactId: string) => void;
  onContactPress?: (contact: Contact) => void;
  /** Set when the caller's Contacts screen is in a system Data List's scoped
   * mode — passed straight through to ContactEditForm's own `listId` prop
   * to enable its Extra Fields section (item 2). */
  contactsDataListId?: string;
  contactsRefreshTrigger?: number; // Trigger refresh when this value changes
  /** Id of the contact currently open elsewhere — shows a visual "active"
   * mark on that row (see ContactsList's own activeContactId doc comment). */
  activeContactId?: string;
  settingsContent?: React.ReactNode; // Settings content to display
  /** Vertical offset (px) from the top of the viewport, on web only — defaults to 180 to
   * sit below the app's fixed header. Screens that hide the header entirely (e.g. the flow
   * editor, which needs the full viewport height for its canvas) pass 0. */
  topOffset?: number;
  /** Overrides the shared/persisted rightPaneWidth for just this screen (e.g.
   * PostsListScreen wants a wider pane to fit a post + its comments feed,
   * without changing the width every other screen using this same pane
   * remembers). Ignored when the pane is user-collapsed. */
  widthOverride?: number;
}

export const RightPane: React.FC<RightPaneProps> = ({
  isVisible = true,
  onClose,
  content = 'contacts',
  customContent,
  tab,
  selectedContact,
  onContactSave,
  onContactDelete,
  onContactPress,
  contactsDataListId,
  contactsRefreshTrigger,
  activeContactId,
  settingsContent,
  topOffset = 180,
  widthOverride,
}) => {
  const styles = useThemedStyles(getStyles);
  const { isRTL, t } = useLocale();
  const assistantTab = useWorkspaceAssistantStore(s => s.assistantTab);
  const setAssistantTab = useWorkspaceAssistantStore(s => s.setAssistantTab);
  const showAssistant = assistantTab || content === 'empty';
  const { rightPaneCollapsed, setRightPaneCollapsed } = useAppStore();
  const rightPaneWidth = useRightPaneWidth(widthOverride);
  // The pane (and the page beside it) widen while the assistant is showing.
  const setAssistantShown = useWorkspaceAssistantStore(s => s.setAssistantShown);
  useEffect(() => { setAssistantShown(showAssistant); }, [showAssistant, setAssistantShown]);
  useEffect(() => () => setAssistantShown(false), [setAssistantShown]);
  const { phoneAccount } = useProfileCompletion();
  const hasPhoneService = phoneAccount?.status === 'active';
  const { phoneCallState, phoneCallMinimized } = useAppContext();
  // Every terminal state (completed/ended/busy/no-answer/failed) stays
  // rendered until the user explicitly dismisses via PhoneCallScreen's
  // Dismiss control — usePhoneDialer no longer auto-resets to 'idle' on a
  // timer, since a completed call still has actionable post-call quick
  // actions (reminder / add to list / change assignment).
  const isCallActive = phoneCallState !== 'idle';

  // Selection mode for bulk contact actions (assign/tag) — owned locally
  // here rather than lifted to every <RightPane> call site, since it's
  // purely a UI concern of the pane's own contacts view.
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const exitSelectionMode = () => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  };

  // Reset whenever the pane closes or navigates away from the contacts view,
  // so re-opening it never resumes a stale selection.
  useEffect(() => {
    if (!isVisible || content !== 'contacts') {
      exitSelectionMode();
    }
  }, [isVisible, content]);

  const handleToggleSelect = (contact: ContactsListContact) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(contact.id)) next.delete(contact.id);
      else next.add(contact.id);
      return next;
    });
  };

  const handleSelectAllFiltered = (contacts: ContactsListContact[]) => {
    setSelectedIds(new Set(contacts.map(c => c.id)));
  };

  const widthAnim = useRef(new Animated.Value(rightPaneCollapsed ? 0 : rightPaneWidth)).current;

  useEffect(() => {
    Animated.timing(widthAnim, {
      toValue: rightPaneCollapsed ? 0 : rightPaneWidth,
      duration: PANE_ANIMATION_DURATION,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: false,
    }).start();
  }, [rightPaneCollapsed, rightPaneWidth, widthAnim]);

  // Continuous subtle pulse on the grabber bar to draw the eye to it —
  // same opacity-loop pattern as PulsingAccentBar.tsx elsewhere in the app.
  const grabberPulse = useRef(new Animated.Value(1)).current;
  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(grabberPulse, { toValue: 0.5, duration: 900, useNativeDriver: true }),
        Animated.timing(grabberPulse, { toValue: 1, duration: 900, useNativeDriver: true }),
      ])
    );
    loop.start();
    return () => loop.stop();
  }, [grabberPulse]);

  if (!isVisible && isWeb) {
    return null;
  }

  // Render different content based on the content prop
  const renderContent = () => {
    switch (content) {
      case 'contacts':
        return (
          <ContactsList
            showHeader={true}
            showSearch={true}
            showFilters={true}
            refreshTrigger={contactsRefreshTrigger}
            onContactPress={onContactPress}
            hasPhoneService={hasPhoneService}
            activeContactId={activeContactId}
            compact
            selectionMode={selectionMode}
            selectedIds={selectedIds}
            onToggleSelect={handleToggleSelect}
            onSelectAllFiltered={handleSelectAllFiltered}
            onEnterSelectionMode={() => setSelectionMode(true)}
            onExitSelectionMode={exitSelectionMode}
          />
        );

      case 'edit-contact':
        return (
          <ContactEditForm
            contact={selectedContact || null}
            onClose={onClose}
            onSave={onContactSave}
            onDelete={onContactDelete}
            compact={true}
            listId={contactsDataListId}
          />
        );

      case 'details':
        // TODO: Implement details view (conversation details, etc.)
        return customContent || null;

      case 'custom':
        return customContent || null;

      case 'empty':
        return null;

      case 'settings':
        return settingsContent || null;

      default:
        return (
          <ContactsList
            showHeader={true}
            showSearch={true}
            showFilters={true}
            refreshTrigger={contactsRefreshTrigger}
            hasPhoneService={hasPhoneService}
            compact
            selectionMode={selectionMode}
            selectedIds={selectedIds}
            onToggleSelect={handleToggleSelect}
            onSelectAllFiltered={handleSelectAllFiltered}
            onEnterSelectionMode={() => setSelectionMode(true)}
            onExitSelectionMode={exitSelectionMode}
          />
        );
    }
  };

  // In RTL: right pane anchors to left edge, border on right side
  // In LTR: right pane anchors to right edge, border on left side
  const rtlStyle = isWeb
    ? {
        [isRTL ? 'left' : 'right']: 0,
        borderLeftWidth: isRTL ? 0 : 1,
        borderRightWidth: isRTL ? 1 : 0,
        borderLeftColor: colors.border,
        borderRightColor: colors.border,
      }
    : {};

  // Starts below the fixed web header (topOffset — the header's real
  // measured height, passed down from ScreenLayout) and spans to the
  // bottom of the viewport, so pane content (including things like the
  // call screen's own minimize button) is never hidden behind the header.
  const offsetStyle = isWeb
    ? { top: topOffset, bottom: 0 }
    : {};

  // Chevron sits on the pane's inner edge (facing main content) — the left
  // edge in LTR, right edge in RTL — outside the animated-width container
  // so it stays clickable as the pane collapses to 0. Full-height grabber
  // bar, not a small floating circle — matches LeftPane's equivalent.
  const grabberSide = isRTL ? 'right' : 'left';
  const chevronIcon = rightPaneCollapsed
    ? (isRTL ? 'chevron-back' : 'chevron-forward')
    : (isRTL ? 'chevron-forward' : 'chevron-back');

  return (
    <Animated.View style={[styles.wrapper, rtlStyle, offsetStyle, { width: widthAnim }]} pointerEvents="box-none">
      <View style={styles.container}>
        {isCallActive && !phoneCallMinimized ? (
          <PhoneCallScreen />
        ) : (
          <>
            {isCallActive && phoneCallMinimized && <PhoneCallMinimizedBar />}
            {/* What is open here (chat, call history, contact, form…) beside the AI assistant: icon + short label. */}
            <View accessibilityRole="tablist" style={[styles.paneTabs, { flexDirection: rowFor(isRTL) }]}>
              {content !== 'empty' && (() => {
                const kind = PANE_TABS[content === 'custom' ? tab || 'details' : content] || PANE_TABS.details;
                const selected = !showAssistant;
                return <TouchableOpacity accessibilityRole="tab" accessibilityState={{ selected }} accessibilityLabel={t(kind.label)} {...(isWeb ? { title: t(kind.label) } as any : {})}
                  style={[styles.paneTab, { flexDirection: rowFor(isRTL) }, selected && styles.paneTabActive]} onPress={() => setAssistantTab(false)}>
                  <Ionicons name={selected ? kind.active : kind.icon} size={16} color={(selected ? colors.primary : colors.text.secondary) as string} />
                  <Text numberOfLines={1} style={[styles.paneTabText, selected && styles.paneTabTextActive]}>{t(kind.label)}</Text>
                </TouchableOpacity>;
              })()}
              <TouchableOpacity accessibilityRole="tab" accessibilityState={{ selected: showAssistant }} accessibilityLabel={t('workspaceAssistant.title')} {...(isWeb ? { title: t('workspaceAssistant.title') } as any : {})}
                style={[styles.paneTab, { flexDirection: rowFor(isRTL) }, showAssistant && styles.paneTabActive]} onPress={() => setAssistantTab(true)}>
                <AiBotIcon size={16} color={(showAssistant ? colors.primary : colors.text.secondary) as string} />
                <Text numberOfLines={1} style={[styles.paneTabText, showAssistant && styles.paneTabTextActive]}>{t('workspaceAssistant.paneTab.assistant')}</Text>
              </TouchableOpacity>
            </View>
            {/* Preserve a partially edited contact/form when the user asks the assistant for help. */}
            {content !== 'empty' && <View style={{ flex: 1, minHeight: 0, display: showAssistant ? 'none' : 'flex' }}>{renderContent()}</View>}
            {showAssistant && <WorkspaceAssistant visible={!rightPaneCollapsed} />}
          </>
        )}
      </View>
      {isWeb && !isMobileBrowser && (
        <TouchableOpacity
          style={[styles.grabberHit, { [grabberSide]: -7 } as any]}
          onPress={() => setRightPaneCollapsed(!rightPaneCollapsed)}
          activeOpacity={0.85}
        >
          <Animated.View style={[styles.grabberIconWrap, { opacity: grabberPulse }]}>
            <Ionicons name={chevronIcon as any} size={12} color={colors.text.onPrimary as string} />
          </Animated.View>
        </TouchableOpacity>
      )}
    </Animated.View>
  );
};

const getStyles = () => StyleSheet.create({
  wrapper: {
    // height: '100%' is native-only — on web the wrapper is positioned via
    // top/bottom (offsetStyle, computed from topOffset), and combining that
    // with height:100% made the box taller than the viewport (100% of the
    // viewport PLUS starting topOffset px down), pushing the footer content
    // below the visible area. top+bottom alone fully determines the box height.
    ...(!isWeb && { height: '100%' }),
    ...(isWeb && {
      position: 'fixed' as any,
      zIndex: 100, // Lower z-index to stay below header
      overflow: 'visible' as any,
    }),
  },
  container: {
    flexDirection: 'column',
    height: '100%',
    width: '100%',
    backgroundColor: colors.background.surface,
    overflow: 'hidden',
  },
  paneTabs: { gap: 4, margin: 8, padding: 3, borderRadius: 10, backgroundColor: colors.background.secondary },
  paneTab: { flex: 1, minHeight: 34, gap: 6, paddingHorizontal: 10, borderRadius: 8, justifyContent: 'center', alignItems: 'center' },
  paneTabActive: { backgroundColor: colors.background.surface, ...(isWeb ? { boxShadow: '0 1px 2px rgba(0,0,0,0.12)' } as any : { elevation: 1 }) },
  paneTabText: { fontSize: 12.5, color: colors.text.secondary, fontFamily: typography.fontFamily.medium, flexShrink: 1 },
  paneTabTextActive: { color: colors.primary, fontFamily: typography.fontFamily.semibold },
  grabberHit: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: 14,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 101,
    ...(isWeb && { cursor: 'pointer' as any }),
  },
  grabberIconWrap: {
    width: 20,
    height: 36,
    borderRadius: 10,
    backgroundColor: colors.brand.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
